import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { MdHome, MdWaterDrop, MdWifi } from "react-icons/md";
import Chip from "@Components/Decor/Chip";
import Skewer from "@Components/Decor/Skewer";
import IconHolder from "@Components/Decor/IconHolder";
import Float from "@Components/Decor/Float";
import BillCard from "../BillCard/BillCard";

const LightIconHolder: FC<{ icon: ReactNode }> = ({ icon }) => (
  <div className="bg-background-primary-lighter text-icon-active outline-background-default flex w-min items-center rounded-full shadow-sm outline-4 outline">
    {icon}
  </div>
);

function TopupIllustration() {
  const { t } = useTranslation();

  return (
    <div
      className="topup-hero-decor-scroll pointer-events-none absolute end-0 top-6 h-20 w-28 shrink-0 lg:relative lg:end-auto lg:top-auto lg:h-64 lg:w-96"
      aria-hidden
    >
      <div className="scale-30 absolute end-0 top-0 h-64 w-96 origin-top-right lg:scale-100 rtl:origin-top-left">
        <Skewer angle={5} className="absolute end-10 top-4 bg-transparent! p-0! not-italic">
          <Float duration={5} delay={-1}>
            <BillCard title={t("Topup.hero.billTitle")} subtitle={t("Topup.hero.billSubtitle")} status={t("Topup.hero.paidWithPoints")} />
          </Float>
        </Skewer>

        <div className="absolute start-6 top-14">
          <Float duration={4} delay={-1.2} distance={-6}>
            <LightIconHolder icon={<MdWaterDrop className="aspect-square h-auto w-8 p-2" />} />
          </Float>
        </div>
        <div className="absolute end-0 top-2">
          <Float duration={4.5} delay={-3} distance={-6}>
            <LightIconHolder icon={<MdHome className="aspect-square h-auto w-8 p-2" />} />
          </Float>
        </div>
        <div className="absolute end-4 top-40">
          <Float duration={3.5} delay={-0.6} distance={-6}>
            <IconHolder icon={<MdWifi className="aspect-square h-auto w-8 p-2" />} />
          </Float>
        </div>
      </div>
    </div>
  );
}

export default function TopupHero() {
  const { t } = useTranslation();

  return (
    <div className="relative flex w-full items-center justify-between gap-8">
      <div className="flex flex-col gap-4">
        <Chip
          title={t("Topup.hero.badge")}
          icon={<div className="h-1.5 w-1.5 rounded-full bg-current" />}
          className="text-xs! outline-none!"
        />
        <h1 className="font-poppins text-font-default text-4xl font-semibold md:text-5xl">{t("Topup.title")}</h1>
        <p className="text-font-subtle text-sm leading-loose md:text-base">
          {t("Topup.hero.subtitle")}{" "}
          <Skewer angle={3} className="px-2 py-1 font-bold!">
            {t("Topup.hero.highlight")}
          </Skewer>
        </p>
      </div>
      <TopupIllustration />
    </div>
  );
}
